import m from 'mithril'

import InputSearch from '@app/components/input-search'
import UploadsContainerOverlay from '@app/pages/origin-destination/components/uploads-container-overlay/uploads-container-overlay.ts';
import ReportsMenuLinkProgress from '@app/pages/reports/components/reports-menu-link-progress/reports-menu-link-progress.ts';

// Lazy-load modals/overlays (only rendered on user interaction)
const _cache = {}
const Lazy = (key, load) => ({
	view: vnode => {
		if (!_cache[key]) {
			_cache[key] = { C: null }
			load().then(mod => { _cache[key].C = mod.default; m.redraw() })
		}
		return _cache[key].C ? m(_cache[key].C, vnode.attrs, vnode.children) : null
	}
})

const AttParamsForm = Lazy('APF', () => import('./components/att-params-form'))
const SystemSettings = Lazy('SS', () => import('@app/components/system-settings/system-settings'))
const UserSettings = Lazy('US', () => import('@app/components/user-settings'))
const Notification = Lazy('N', () => import('@app/components/notifications/notifications'))
const HelpCenters = Lazy('HC', () => import('@app/components/help-centers'))
const Fullscreen = Lazy('FS', () => import('../space-time-diagram/fullscreen'))
const FullscreenLoading = Lazy('FSL', () => import('../space-time-diagram/fullscreen/loading'))
const AbacusFullscreenChart = Lazy('AFC', () => import('../report-abacus/components/fullscreen-chart/fullscreen-chart'))

import SpaceTimeModel from '@app/models/space-time';
import LitmusChartModel from '@app/models/litmus-chart';
import AbacusChartModel from '@app/models/abacus-chart';
import UserModel from '@app/models/users'
import Notifier from '@app/components/notify';

import FlowLabsLogo from '@shared/icons/flow-labs-logo'
import IconGear from '@app/icons/gear'
import IconNotification from '@app/icons/notification'
import IconHelpCenter from '@app/icons/help-center'
import IconApiPanel from '@app/icons/api-panel'
import router from '@app/router'

import ReportsProgressEmitter from '#frontend/pages/reports/reports-progress-emitter.ts';
import { $notifications_count } from '#frontend/stores/notifications.stores.ts';

import s from './styles.module.scss'


const Navbar = {}

Navbar.oninit = async vnode => {
	vnode.state.init = true;
	vnode.state.notifications_count_unsubscribe = null
	vnode.state.notifications_count = 0


    // vnode.state.is_settings_open = false
    // vnode.state.closeSettings = () => vnode.state.is_settings_open = false

    // vnode.state.is_uploads_modal_open = false
    // vnode.state.closeUploadModals = () => vnode.state.is_uploads_modal_open = false

    vnode.state.openUserSettingsModals = () => {
    	router.currentRoute().navigate({query: {user_settings:1},  options: { preserve: true } })
    }

    vnode.state.closeUserSettingsModals = () => {
    	router.currentRoute().navigate({query: {user_settings:undefined},  options: { preserve: true } })
	}

	// vnode.state.is_settings_profile_open = false
	// vnode.state.closeProfileSettingModals = () => vnode.state.is_settings_profile_open = false

	vnode.state.openSystemSettingModals = () => {
		router.currentRoute().navigate({query: {system_settings:1},  options: { preserve: true } })
	}

	vnode.state.closeSystemSettingModals = () => {
		router.currentRoute().navigate({query: {system_settings:undefined},  options: { preserve: true } })
	}

	vnode.state.is_notification_open = false
    vnode.state.closeNotificationsModals = () => vnode.state.is_notification_open = false

	vnode.state.is_help_center_open = false
	vnode.state.closeHelpCentersModals = () => vnode.state.is_help_center_open = false

	await UserModel.fetch({ customer_id: vnode.attrs.customer_id })
		.then(() => m.redraw())
		.catch(err => console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err))
}

Navbar.onremove = (vnode) => {
	vnode.state.notifications_count_unsubscribe?.()

    clearInterval(vnode.state.interval);
    vnode.state.interval = false;
};

Navbar.view = vnode => {
	const current_route = router.currentRoute()
	const {user_settings, system_settings} = current_route.query
	const {network_id} = current_route.params

	const {
		customer_id,
		customer_name,

		timezone_iana,
		latest_metrics_date,
		tod_alerts,

		custom_time_control_component,
		disable_time_controls,
		disable_date_range,
		fixed_date_range_days,
		disable_custom_tod,
		tods_loading,
		persist_tod_id,
		disable_rt,
		disable_tod_settings,

		hide_search,
		api_panel_active,
	} = vnode.attrs

	const count = vnode.state.notifications_count
	const { checkCerberusPermission, checkReportsPermission } = UserModel;

	let first_name = ""
	let email
	let is_admin
	let is_super_admin

	if (UserModel.user){
		first_name = UserModel.user.first_name
		email = UserModel.user.email
		is_admin = UserModel.is_admin
		is_super_admin = UserModel.is_super_admin
	}

	const links = (customer_id) ? [
		{
			key: 'system_link',
			title: 'Map View',
			link: router.routes.system.useNavigation({params: {customer_id: customer_id}}),
			active: ['system', 'region', 'subregion', 'network', 'signal', 'phase', 'intersection', 'roadway', 'route', 'workzone', 'road_segment'].some(s => current_route?.name?.endsWith(s)),
		},
		checkCerberusPermission() && {
			key: 'cerberus_link',
			title: 'Cerberus',
			link: router.routes.cerberus.useNavigation({params: {customer_id: customer_id}}),
			active: ['cerberus'].includes(current_route.name),
		},
		// checkCerberusPermission() && {
		// 	key: 'cerberus_v2_link',
		// 	title: 'Cerberus V2',
		// 	link: router.routes.cerberus_v2.useNavigation({params: {customer_id: customer_id}}),
		// 	active: ['cerberus_v2'].includes(current_route.name),
		// },
		checkReportsPermission() && {
			key: 'reports_link',
			title: 'Reports',
			link: router.routes.reports.useNavigation({ params: { customer_id } }),
			active: current_route.name === 'reports',
			component: m(ReportsMenuLinkProgress, {}, 'Reports')
		},
	].filter(x => x) : []

	if (is_super_admin && current_route.name === 'root') {
		links.push({
			key: 'data_monitoring',
			title: 'Data Monitoring',
			link: router.routes.data_monitoring_feeds.useNavigation(),
			active: current_route.path.indexOf(router.routes.data_monitoring_redirect.path) === 0
		});
	}


    if (vnode.state.init) {

		if (customer_id) {
			UserModel.fetchMe(customer_id).then(() => m.redraw());

			UserModel.fetchUserSettings(customer_id).then(() => m.redraw());

			vnode.state.notifications_count_unsubscribe = $notifications_count.subscribe((new_count) => {
				if (vnode.state.notifications_count !== new_count) {
					vnode.state.notifications_count = new_count
					m.redraw()
				}
			})

			if (UserModel.checkODReportsPermission()) {
				ReportsProgressEmitter.setCustomerId(customer_id)
			}

			vnode.state.init = false;
		}
		// System Settings is gated on customer_id at render time, so a deep link
		// (?system_settings=1) survives a cold reload and opens once the customer loads.

    }




	return m('nav', {class: s.navbar}, [



		m('div', {class: ([s.section, s.section_1]).join(' ')}, [

			m('a', {
				class: s.link,
				href: '/',
                ariaLabel: 'Home',
			}, [
				m('div', {class: s.logo}, FlowLabsLogo)
			]),
			links.map(link => {
				const classes = [s.link]
				if(link.active) classes.push(s.active)
				return m('a', {
                    key: link.key,
				    href: link.link.href,
				    class: classes.join(' '),
				    onclick: e => {
				    	if (!router.active) return
				    	e.preventDefault()

						// prevent navigation to cerberus if already within cerberus
						// if not prevented, the sigops networks view will be loaded but the sidebar active item will not be updated
						if (link.key === 'cerberus_link' && current_route.name === 'cerberus') {
							return
						}

				    	link.link()
				    }
				}, link.component ?? link.title)
			})
		]),



		custom_time_control_component,

        customer_id && !disable_time_controls && m(AttParamsForm, {
            customer_id: customer_id,
            network_id,
            tods: vnode.attrs.tods,
            timezone_iana: timezone_iana,
			latest_metrics_date,
			tod_alerts,

			disable_date_range,
			fixed_date_range_days,
			disable_custom_tod,
			disable_tod_settings: network_id && disable_time_controls,
			tods_loading,
			persist_tod_id,
			disable_rt,
			rt_last_refreshed: vnode.attrs.rt_last_refreshed,
        }),



		m('div', {class: ([s.section, s.section_main, s.section_3]).join(' ')}, [

			customer_id && !hide_search && m(InputSearch, {
				customer_id,
			}),

			(UserModel.has_api_access || api_panel_active) && customer_id && m('button', {
				class: [s.icon_api_panel, api_panel_active && s.icon_api_panel_active].filter(Boolean).join(' '),
				ariaLabel: 'API Panel',
				onclick: () => { window.location.href = `/api-panel/${customer_id}/keys` }
			}, IconApiPanel),

			m('button', {
				class: s.icon_settings,
                ariaLabel: 'Toggle Help center',
                onclick: e => vnode.state.is_help_center_open = !vnode.state.is_help_center_open
			}, IconHelpCenter),

			customer_id && m('button', {
				class: s.icon_settings,
                ariaLabel: 'Toggle System Settings',
				// onclick: e => {
				// 	router.currentRoute().navigate({
                //         options: { preserve: true},
                //         query: { settings: 'user-settings'},
                //     })
				// }
                onclick: vnode.state.openSystemSettingModals,
			}, IconGear),

			//Notification
			customer_id && m('button', {
				class: [s.icon_settings,s.icon_notifications].join(' '),
                ariaLabel: 'Toggle Notification bell',
                onclick: () => {
					vnode.state.is_notification_open = !vnode.state.is_notification_open
				}
			},[
				IconNotification,
				count ? m('div', {
					class: s.notification_count,
					ariaLabel: 'Toggle Notification bell',
				}, m('span', count > 99 ? '99+' : count)) : null,
			]),


			//Profile setting
			m('button', {
				class: s.icon_settings,
                ariaLabel: 'User Setting',
                onclick: vnode.state.openUserSettingsModals
			}, [m("span",
				{
					class: s.icon_user
				},
				(() => {
					const photo = localStorage.getItem('__flow_labs_user_photo');
					return photo
						? m('img', { src: photo, style: 'width:100%;height:100%;object-fit:cover;border-radius:50%;' })
						: Array.from(first_name || email || "")[0];
				})())
			]),

		]),




		// Settings Modal. Has built in routing, so it just listens for query parameters changes and does it's thing.
		// vnode.state.is_settings_open && m(SettingsModal,
		// 	{
		// 		closeSettings: vnode.state.closeSettings,
		// 		customer_id,
		// 		openUploadModal : () => {
		// 			vnode.state.is_uploads_modal_open = true;
		// 		    vnode.state.closeSettings()
		// 		},
		// 		openThreshHoldModal : () =>{
		// 			vnode.state.is_settings_system_open = true;
		// 		    vnode.state.closeSettings()
		// 		}
		// 	}
		// ),

		// vnode.state.is_settings_profile_open && m(ProfileModal,
		// 	{
		// 		closeSettings: vnode.state.closeProfileSettingModals,
        //         openUserSettingsModal : () => {
		// 			user_settings = true;
		// 		    vnode.state.closeProfileSettingModals()
		// 		}
		// 	}
		// ),

		// vnode.state.is_uploads_modal_open && m(ManualDataUploads,
		// 	{
		// 		closeSettings: vnode.state.closeUploadModals,
		// 	    customer_id
		// 	}
		// ),

        user_settings && m(UserSettings,
			{
                closeSettings: vnode.state.closeUserSettingsModals,
			    customer_id
            }
        ),

        system_settings && customer_id && m(SystemSettings,
			{
                closeSettings: vnode.state.closeSystemSettingModals,
				customer_id,
				customer_name,
				is_admin,
				is_super_admin
            }
        ),

		vnode.state.is_notification_open && m(Notification,{
			customer_id,
			closeModal : vnode.state.closeNotificationsModals
		}),

		vnode.state.is_help_center_open && m(HelpCenters,{
			customer_id,
			is_super_admin,
			closeSettings : vnode.state.closeHelpCentersModals
		}),

        SpaceTimeModel.showModal &&
            (SpaceTimeModel.loading_optimus
                ? m(FullscreenLoading, {
                      closeSettings: () => {
                          SpaceTimeModel.setShowModal(false);
                      },
                  })
                : m(Fullscreen, {
                      closeSettings: () => {
                          SpaceTimeModel.setShowModal(false);
                      },
                  })),


			LitmusChartModel.showModal &&
				m(LitmusFullscreenChart, {
					closeModal: () => {
						LitmusChartModel.setShowModal(false);
					},
				}),

			AbacusChartModel.showModal &&
				m(AbacusFullscreenChart, {
					closeModal: () => {
						AbacusChartModel.setShowModal(false);
					},
				}),

		m(Notifier),

		m(UploadsContainerOverlay)
	])
}

export default Navbar
